// Client-side checks for the PowerTranz card form.
//
// Stripe's Elements validate themselves; the PowerTranz fields are plain inputs,
// so without this a mistyped card number went all the way to the gateway and
// came back as a decline — which the checkout counts as a failed purchase
// attempt. These checks catch what can be known without the issuer: a number
// that fails its own checksum, a card that has already expired, a CVC of the
// wrong length for the brand.
//
// The server re-validates everything (PaymentController::powerTranzAuth). This
// is for the buyer's benefit, not a security boundary.

// Accepted PAN lengths and CVC length per brand. Anything unrecognised falls
// back to the ISO/IEC 7812 range the backend accepts (12–19) rather than being
// rejected — an unknown BIN is not a wrong number.
const BRANDS = [
    { name: 'amex', pattern: /^3[47]/, lengths: [15], cvcLength: 4 },
    { name: 'visa', pattern: /^4/, lengths: [13, 16, 19], cvcLength: 3 },
    // 2-series is really 2221–2720; `2[2-7]` is deliberately a touch wide —
    // nothing else issues in that range and the checksum still has to pass.
    { name: 'mastercard', pattern: /^(5[1-5]|2[2-7])/, lengths: [16], cvcLength: 3 },
    { name: 'discover', pattern: /^(6011|65|64[4-9])/, lengths: [16, 17, 18, 19], cvcLength: 3 },
];

export const CARDHOLDER_NAME_MAX = 100;

const digitsOf = (value) => String(value ?? '').replace(/\D/g, '');

export function detectCardBrand(number) {
    const digits = digitsOf(number);
    return BRANDS.find((brand) => brand.pattern.test(digits)) || null;
}

export function luhnCheck(number) {
    const digits = digitsOf(number);
    if (!digits) return false;

    let sum = 0;
    let double = false;
    for (let i = digits.length - 1; i >= 0; i -= 1) {
        let n = Number(digits[i]);
        if (double) {
            n *= 2;
            if (n > 9) n -= 9;
        }
        sum += n;
        double = !double;
    }
    return sum % 10 === 0;
}

function validateNumber(number) {
    const digits = digitsOf(number);
    if (!digits) return 'Enter your card number.';

    const brand = detectCardBrand(digits);
    const lengthOk = brand
        ? brand.lengths.includes(digits.length)
        : digits.length >= 12 && digits.length <= 19;

    if (!lengthOk) {
        return digits.length < (brand ? Math.min(...brand.lengths) : 12)
            ? 'Your card number is incomplete.'
            : 'Your card number is too long.';
    }
    if (!luhnCheck(digits)) return 'Your card number is invalid. Check the digits and try again.';
    return '';
}

function validateExpiry(expiry, now) {
    const digits = digitsOf(expiry);
    if (!digits) return 'Enter the card expiry date.';
    if (digits.length !== 4) return 'Enter the card expiry as MM / YY.';

    const month = Number(digits.slice(0, 2));
    const year = 2000 + Number(digits.slice(2));
    if (month < 1 || month > 12) return 'Enter a valid expiry month (01–12).';

    // A card is good through the LAST day of its expiry month.
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth() + 1;
    if (year < currentYear || (year === currentYear && month < currentMonth)) {
        return 'This card has expired.';
    }
    // Issuers do not print dates this far out; it is a typo (e.g. 12 / 62).
    if (year > currentYear + 20) return 'Enter a valid expiry year.';
    return '';
}

function validateCvv(cvv, number) {
    const digits = digitsOf(cvv);
    if (!digits) return 'Enter the card security code.';

    const brand = detectCardBrand(number);
    if (brand) {
        if (digits.length !== brand.cvcLength) {
            return brand.cvcLength === 4
                ? 'American Express security codes are 4 digits.'
                : 'The security code is 3 digits.';
        }
        return '';
    }
    return digits.length === 3 || digits.length === 4 ? '' : 'The security code is 3 or 4 digits.';
}

function validateCardholderName(name) {
    const trimmed = String(name ?? '').trim();
    if (!trimmed) return 'Enter the name on the card.';
    if (trimmed.length < 2 || !/\p{L}/u.test(trimmed)) return 'Enter the name as it appears on the card.';
    if (trimmed.length > CARDHOLDER_NAME_MAX) return `The name must be ${CARDHOLDER_NAME_MAX} characters or fewer.`;
    // A card number typed into the name box — autofill does this — must not be
    // sent on as a name, where it would be stored and logged in the clear.
    if (digitsOf(trimmed).length >= 12) return 'Enter the name on the card, not the card number.';
    return '';
}

/**
 * One field's error message, or '' when it is valid.
 * `field` is a key of the cardDetails state: number | expiry | cvv | cardholderName.
 */
export function validateCardField(field, cardDetails, now = new Date()) {
    switch (field) {
        case 'number':
            return validateNumber(cardDetails.number);
        case 'expiry':
            return validateExpiry(cardDetails.expiry, now);
        case 'cvv':
            return validateCvv(cardDetails.cvv, cardDetails.number);
        case 'cardholderName':
            return validateCardholderName(cardDetails.cardholderName);
        default:
            return '';
    }
}

// In the order the fields appear, so the first key is the first one on screen.
export const CARD_FIELDS = ['number', 'expiry', 'cvv', 'cardholderName'];

/** Every failing field → message. Empty object when the card is good to send. */
export function validateCard(cardDetails, now = new Date()) {
    const errors = {};
    CARD_FIELDS.forEach((field) => {
        const message = validateCardField(field, cardDetails, now);
        if (message) errors[field] = message;
    });
    return errors;
}
